import { ReactNode, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { colors, screen } from 'components/styles/variables';
import StickyHeaderContainer from 'components/Shared/StickyHeaderContainer';
import SidebarAds from 'components/Ad/SidebarAds';
import Spacer from 'components/Shared/Spacer';
import Ad from 'components/Ad';

import { setScrollToVehicleInfo } from 'redux-implementations/vdpScroll';
import { MEDIUM, SMALL, X_SMALL } from 'lib/DeviceScreen';
import { TINY_RECTANGLE_2 } from 'lib/GoogleAds/Sizes';
import State from 'types/State';
import { useMedia, useWindowScroll } from 'react-use';
import DirectToVdpContext from 'contexts/DirectToVdpContext';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';

type ContactBarRenderProp = ({
  className,
  styles,
}: {
  className?: string;
  styles?: React.CSSProperties;
}) => JSX.Element;

interface LayoutProps {
  dealerRemoteId?: string;
  photoCarousel: JSX.Element;
  vehicleSnapshot: JSX.Element;
  priceAnalysis: JSX.Element;
  priceHistory: JSX.Element;
  vehicleInformation: JSX.Element;
  keyFeatures: JSX.Element;
  sellerProfile: JSX.Element;
  leadForm: JSX.Element;
  listingsCarousel: ReactNode;
  photoLightBox: JSX.Element;
  contactBar: ContactBarRenderProp;
  stickyHeader: JSX.Element;
}

function VdpLayout({
  dealerRemoteId,
  photoCarousel,
  vehicleSnapshot,
  priceAnalysis,
  priceHistory,
  vehicleInformation,
  keyFeatures,
  sellerProfile,
  leadForm,
  listingsCarousel,
  photoLightBox,
  contactBar,
  stickyHeader,
}: LayoutProps): JSX.Element {
  const scrollToVehicleInfo = useSelector<State>((state) => state.vdpScroll.scrollToVehicleInfo);
  const dispatch = useDispatch();

  useEffect(() => {
    if (scrollToVehicleInfo) {
      const elem = document.getElementById('vehicle-information');
      elem?.scrollIntoView({ behavior: 'auto' });
      dispatch(setScrollToVehicleInfo(false));
    }
  }, [scrollToVehicleInfo, dispatch]);

  const shouldShowStickyHeader = useWindowScroll().y >= 1000;

  const { isDirectToVdp } = useContext(DirectToVdpContext);

  const { getDecision } = useOptimizelyDecisions();
  const vdpStickyCta4 = getDecision('vdpStickyCta4');
  const smsFunctionality = getDecision('smsFunctionality');

  const { isMobile } = useDeviceScreen();
  const isPhone = useMedia(`(max-width: ${SMALL.cssMaxWidth})`);
  const leadFormMaxWidth = isPhone && smsFunctionality ? '100%' : '456px';

  return (
    <div className="vdp">
      <div className="sticky-header">
        {<StickyHeaderContainer isShowing={shouldShowStickyHeader} children={stickyHeader} />}
      </div>
      {isMobile &&
        vdpStickyCta4 &&
        contactBar({
          styles: {
            position: 'fixed',
            bottom: 0,
            zIndex: 999,
            padding: 16,
            boxShadow: '0px -4px 8px 0px rgba(0, 0, 0, 0.05)',
            borderTopStyle: 'solid',
            borderColor: colors.G1,
            borderWidth: '1px',
          },
        })}
      <div className="page">
        <div className="flex-container">
          <div className="left-container">
            <div className="vehicle-content">
              {photoCarousel}
              <Spacer size="16px" />
              <div className="mobile-layout">{vehicleSnapshot}</div>
              {!vdpStickyCta4 && (
                <div className="mobile-layout">
                  {contactBar({ className: classNames('contact-bar') })}
                </div>
              )}
              {isDirectToVdp && listingsCarousel && (
                <>
                  <div className="similar-listings-container">
                    <Spacer size="16px" smSize="24px" />
                    {listingsCarousel}
                  </div>
                  <Spacer size="16px" smSize="24px" />
                  <hr className="always-line-break" />
                </>
              )}
              <Spacer size="0px" smSize="8px" />
              {smsFunctionality && isMobile && <div className="full-width-line-break" />}
              <div className="mobile-layout">{priceAnalysis}</div>
              <Spacer size="0px" smSize="25px" />
              <hr className="line-break" />
              <Spacer size="0px" smSize="8px" />
              <div className="mobile-layout">{priceHistory}</div>
              {smsFunctionality && isMobile ? (
                <div className="full-width-line-break" />
              ) : (
                <hr className="line-break" />
              )}
              <Spacer size="0px" smSize="28px" />
              <Ad
                adConfig={{
                  targeting: {
                    inLine: 5,
                    sellerId: dealerRemoteId,
                  },
                  dimensions: [
                    [320, 50],
                    [300, 250],
                  ],
                  screenSizes: [MEDIUM, SMALL, X_SMALL],
                }}
                placeholderSize={TINY_RECTANGLE_2}
              />
              {smsFunctionality && isMobile ? (
                <div className="full-width-line-break" />
              ) : (
                <hr className="line-break" />
              )}
              <div className="mobile-layout" id="vehicle-information">
                {vehicleInformation}
              </div>
              <Spacer size="16px" smSize="36px" />
              <Ad
                adConfig={{
                  targeting: {
                    inLine: 6,
                    sellerId: dealerRemoteId,
                  },
                  dimensions: [
                    [320, 50],
                    [300, 250],
                  ],
                  screenSizes: [MEDIUM, SMALL, X_SMALL],
                }}
                placeholderSize={TINY_RECTANGLE_2}
              />
              <hr className="line-break" />
              <div className="mobile-layout">{keyFeatures}</div>
              <Ad
                adConfig={{
                  targeting: {
                    inLine: 8,
                    sellerId: dealerRemoteId,
                  },
                  dimensions: [
                    [320, 50],
                    [300, 250],
                  ],
                  screenSizes: [MEDIUM, SMALL, X_SMALL],
                }}
                placeholderSize={TINY_RECTANGLE_2}
              />
              <Spacer size="0px" smSize="24px" />
              <hr className="line-break" />
              <Spacer size="16px" smSize="28px" />
            </div>
            <div className="mobile-layout">{sellerProfile}</div>
            <Spacer size="16px" smSize="24px" />
          </div>
          <Spacer size="0px" mdSize="32px" />
          <hr className="line-break" />
          <div className="right-rail mobile-layout flex-container">
            {leadForm}
            <Spacer size="57px" />
            <SidebarAds sellerId={dealerRemoteId} />
          </div>
        </div>
        {listingsCarousel && (
          <>
            <hr className="outer-line-break" />
            <div className="similar-listings-container">
              <Spacer size="16px" smSize="24px" />
              {listingsCarousel}
            </div>
          </>
        )}
        <Spacer size="20px" />
      </div>
      {photoLightBox}
      <style jsx>{`
        .vdp {
          display: flex;
          flex-direction: column;
          align-items: center;
          letter-spacing: 0.2px;
        }

        .sticky-header {
          z-index: 999;
        }

        .page {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        @media (min-width: ${screen.smMin}) {
          .page {
            padding-top: 24px;
            width: 750px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .page {
            width: 970px;
          }
        }

        .flex-container {
          display: flex;
          justify-content: center;
          flex-direction: column;
        }

        @media (min-width: ${screen.mdMin}) {
          .flex-container {
            flex-direction: row;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .page {
            width: 1160px;
          }
        }

        .left-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        }

        @media (min-width: ${screen.smMin}) {
          .left-container {
            padding: 0px 16px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .left-container {
            padding: 0;
            width: 555px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .left-container {
            width: 672px;
          }
        }

        .vehicle-content {
          display: flex;
          flex-direction: column;
        }

        .right-rail {
          flex-direction: column;
          align-items: center;
          justify-content: start;
          width: 100%;
          max-width: ${leadFormMaxWidth};
        }

        .similar-listings-container {
          display: flex;
          flex-direction: column;
          width: 100%;
          padding-left: 16px;
          max-width: 1160px;
        }

        @media (min-width: ${screen.mdMin}) {
          .similar-listings-container {
            padding: 0;
          }
        }

        .full-width-line-break {
          width: 100%;
          height: 4px;
          background-color: ${colors.G1};
          margin-top: 16px;
        }

        .always-line-break {
          display: block;
          height: 1px;
          border-top: none;
          background-color: ${colors.G2};
          margin: 0;
        }

        .line-break,
        .outer-line-break {
          display: block;
          height: 1px;
          border-top: none;
          background-color: ${colors.G2};
          margin: 0 16px;
        }

        @media (min-width: ${screen.smMin}) {
          .line-break,
          .outer-line-break {
            display: block;
            height: 1px;
            border-top: none;
            background-color: ${colors.G2};
            margin: 0;
          }

          .outer-line-break {
            margin: 15px 16px 0 16px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .line-break,
          .outer-line-break {
            margin: 0;
          }
        }

        .mobile-layout {
          padding: 0px 16px;
        }

        @media (min-width: ${screen.smMin}) {
          .mobile-layout {
            padding: 0px;
          }
        }
      `}</style>
    </div>
  );
}

export default VdpLayout;
